import {Component, forwardRef, Input, OnInit, QueryList, ViewChildren} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR, Validators} from '@angular/forms';
import {IonInput} from '@ionic/angular';

@Component({
    selector: 'registrationcode',
    template: `
        <div class="d-flex flex-row">
            @for (control of controls; let i = $index; track i) {
                @if (i > 0 && i % 3 === 0) {
                    <ion-text>-</ion-text>
                }

                <ion-input color="light"
                           inputmode="numeric"
                           pattern="[^\d]{1}"
                           [formControl]="control"
                           #controls
                           (ionFocus)="onFocus(i)"
                           (ionBlur)="onBlur(i)"
                           (keyup)="onKeyUp($event, i)"
                           [ngClass]="{'border-black': i === focussed, 'border-danger': !control.valid && !control.untouched, 'border-left': i % 3 === 0}"
                           class="monospaced border border-dark ion-text-center bg-light">
                </ion-input>
            }
        </div>
    `,
    styles: [`
        ion-text {
            margin: 0 12px;
            line-height: 48px;
        }

        ion-input {
            font-size: 20px;
            border-left-style: none;
            /*border-top-style: none;*/
            
        }
        
        .border-left {
            border-left-style: solid;
        }
    `],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => RegistrationCodeComponent),
        multi: true
    }],
    standalone: false
})
export class RegistrationCodeComponent implements ControlValueAccessor, OnInit {
    @ViewChildren('controls') controlElements: QueryList<IonInput>;
    public form: FormGroup;
    public controls: FormControl[];
    public focussed: number;

    @Input()
    public label: string;


    constructor(private formBuilder: FormBuilder) {
    }

    ngOnInit(): void {
        this.controls = Array(9)
            .fill('')
            .map((_, i) => {
                const control = this.formBuilder.control(null, [Validators.required, Validators.minLength(1), Validators.maxLength(1)]);
                control.valueChanges.subscribe((value) => {
                    // sanitize the input
                    const sanitized: string = value?.replace(/[^\d]+/g, '');
                    if (sanitized) {
                        control.patchValue(sanitized.substring(0, 1), {
                            emitEvent: false
                        });
                        if (sanitized.length > 1) {
                            // must be pasted input
                            if ((i + 1) < this.controls.length) {
                                // patching the next control will trigger the value changes event
                                this.controls[i + 1].patchValue(sanitized.substring(1));
                            }
                        }
                    }
                    this.controlElements?.get(i).setFocus();
                    this.propagateChange(this.controls
                        .filter(control => control.valid)
                        .map(control => control.value)
                        .reduce((total, value, index) => {
                            let result = total;
                            if (index > 0 && index % 3 === 0) {
                                result = result + '-';
                            }
                            return result ? result + value : value;
                        }, undefined));
                    this.propagateTouch();
                });
                return control;
            });
        this.form = this.formBuilder.group(Object.assign({}, this.controls));
    }

    ionViewDidEnter() {
        this.controlElements.get(0).setFocus();
    }

    writeValue(_: string) {
        this.controls.forEach(control => control.reset(null, {
            emitEvent: false
        }));
    }

    propagateChange = (_: any) => {
    };
    propagateTouch = () => {
    };

    registerOnChange(fn) {
        this.propagateChange = fn;
    }

    registerOnTouched(fn) {
        this.propagateTouch = fn;
    }

    onKeyUp(event, index: number) {
        if (isNaN(parseInt(event.key, 10)) && event.key !== 'Backspace') {
            event.preventDefault();
        } else if (event.key === 'Backspace') {
            // move to previous
            if (index > 0) {
                this.controls[index - 1].reset();
                const input: IonInput = this.controlElements.get(index - 1);
                input.setFocus();
            }
        } else if (this.controls[index].valid && index < (this.controlElements.length - 1)) {
            // move to the next
            const input: IonInput = this.controlElements.get(index + 1);
            input.setFocus();
            input.getInputElement().then(element => {
                element.select();
            });
        }
    }

    onFocus(index: number) {
        this.focussed = index;
    }

    onBlur(index: number) {
        if (this.focussed === index) {
            this.focussed = undefined;
        }
    }
}
